import React, { useState, useRef, useEffect } from 'react';
import { HexColorPicker, HexAlphaColorPicker } from 'react-colorful';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faTimes, faPlus, faMinus, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { Column, Label } from '../../styles/WidgetCustomizer.styles';
import {
    ColorPickerContainer,
    ColorInputContainer,
    ColorCode,
    ColorSquare,
    ColorPickerPopover,
    ColorPickerContent,
    DragHandle,
    CloseButton,
    ColorSwitcher,
    ColorTypeButton,
    GradientStops,
    StopsList,
    StopItem,
    StopColor,
    StopPercentage,
    StopActions,
    StopButton,
    AngleInput,
    AngleControls,
    AngleLabel,
    AngleValue,
    GradientTypeWrapper,
    GradientTypeSelect,
    AngleUnitWrapper,
    AngleUnitSelect
} from './ColorPicker.styles';

const ColorPicker = ({ label, color, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isGradient, setIsGradient] = useState(false);
    const [isInitialPosition, setIsInitialPosition] = useState(true);
    const [gradientAngle, setGradientAngle] = useState(90);
    const [stops, setStops] = useState([
        { color: '#FFFFFF', position: 0 },
        { color: '#000000', position: 100 }
    ]);
    const [selectedStop, setSelectedStop] = useState(0);
    const [gradientType, setGradientType] = useState('linear');
    const [angleUnit, setAngleUnit] = useState('deg');
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);

    useEffect(() => {
        if (isOpen && isInitialPosition && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const popoverWidth = 220;
            const popoverHeight = isGradient ? 400 : 300;

            let left = rect.right + 8;
            let top = rect.top;

            if (left + popoverWidth > windowWidth) {
                left = rect.left - popoverWidth - 8;
            }
            if (top + popoverHeight > windowHeight) {
                top = windowHeight - popoverHeight - 8;
            }

            left = Math.max(8, left);
            top = Math.max(8, top);

            setPosition({ top, left });
            setIsInitialPosition(false);
        }
    }, [isOpen, isInitialPosition, isGradient]);

    useEffect(() => {
        if (!isOpen) {
            setIsInitialPosition(true);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && popoverRef.current) {
                const windowWidth = window.innerWidth;
                const windowHeight = window.innerHeight;
                const popoverRect = popoverRef.current.getBoundingClientRect();

                let newLeft = e.clientX - dragOffset.x;
                let newTop = e.clientY - dragOffset.y;

                newLeft = Math.min(Math.max(0, newLeft), windowWidth - popoverRect.width);
                newTop = Math.min(Math.max(0, newTop), windowHeight - popoverRect.height);

                setPosition({ left: newLeft, top: newTop });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    const handleMouseDown = (e) => {
        if (popoverRef.current) {
            const rect = popoverRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    };

    const handleInputChange = (e) => {
        const newColor = e.target.value;
        if (newColor.match(/^#[0-9A-Fa-f]{0,8}$/)) {
            onChange(newColor);
        }
    };

    const convertAngleToUnit = (value, fromUnit, toUnit) => {
        // First convert to degrees
        let degrees;
        switch (fromUnit) {
            case 'rad':
                degrees = value * (180 / Math.PI);
                break;
            case 'turn':
                degrees = value * 360;
                break;
            case 'grad':
                degrees = value * 0.9;
                break;
            default:
                degrees = value;
        }

        // Then convert to target unit
        switch (toUnit) {
            case 'rad':
                return degrees * (Math.PI / 180);
            case 'turn':
                return degrees / 360;
            case 'grad':
                return degrees / 0.9;
            default:
                return degrees;
        }
    };

    const handleAngleChange = (e) => {
        const newValue = parseInt(e.target.value) || 0;
        const normalizedValue = ((newValue % 360) + 360) % 360;
        setGradientAngle(normalizedValue);
        if (isGradient) {
            updateGradient(stops, normalizedValue);
        }
    };

    const updateGradient = (newStops, angle = gradientAngle) => {
        let gradientString;
        switch (gradientType) {
            case 'radial':
                gradientString = `radial-gradient(circle, ${newStops
                    .map(stop => `${stop.color} ${stop.position}%`)
                    .join(', ')})`;
                break;
            case 'conic':
                gradientString = `conic-gradient(from ${angle}deg at center, ${newStops
                    .map(stop => `${stop.color} ${stop.position}%`)
                    .join(', ')})`;
                break;
            default: // linear
                gradientString = `linear-gradient(${angle}deg, ${newStops
                    .map(stop => `${stop.color} ${stop.position}%`)
                    .join(', ')})`;
        }
        onChange(gradientString);
    };

    const addStop = () => {
        const newStops = [...stops];
        const lastStop = newStops[newStops.length - 1];
        const secondLastStop = newStops[newStops.length - 2];
        const newPosition = lastStop.position - (lastStop.position - secondLastStop.position) / 2;

        newStops.splice(newStops.length - 1, 0, {
            color: lastStop.color,
            position: newPosition
        });

        setStops(newStops);
        updateGradient(newStops);
    };

    const removeStop = (index) => {
        if (stops.length > 2) {
            const newStops = stops.filter((_, i) => i !== index);
            setStops(newStops);
            updateGradient(newStops);
        }
    };

    const updateStop = (index, updates) => {
        const newStops = [...stops];
        newStops[index] = { ...newStops[index], ...updates };
        newStops.sort((a, b) => a.position - b.position);
        setStops(newStops);
        updateGradient(newStops);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpen &&
                popoverRef.current &&
                !popoverRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <Column>
            <Label>{label}</Label>
            <ColorInputContainer ref={triggerRef}>
                <ColorCode
                    value={color}
                    onChange={handleInputChange}
                    placeholder="#000000"
                />
                <ColorSquare
                    color={color}
                    onClick={() => setIsOpen(!isOpen)}
                />
            </ColorInputContainer>
            {isOpen && (
                <ColorPickerPopover
                    ref={popoverRef}
                    style={{
                        top: position.top,
                        left: position.left
                    }}
                >
                    <DragHandle onMouseDown={handleMouseDown}>
                        <FontAwesomeIcon icon={faGripLines} />
                        <CloseButton onClick={() => setIsOpen(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </CloseButton>
                    </DragHandle>
                    <ColorSwitcher>
                        <ColorTypeButton
                            active={!isGradient}
                            onClick={() => setIsGradient(false)}
                        >
                            Solid
                        </ColorTypeButton>
                        <ColorTypeButton
                            active={isGradient}
                            onClick={() => setIsGradient(true)}
                        >
                            Gradient
                        </ColorTypeButton>
                    </ColorSwitcher>
                    <ColorPickerContent>
                        {isGradient ? (
                            <>
                                <AngleInput>
                                    <GradientTypeWrapper>
                                        <GradientTypeSelect
                                            value={gradientType}
                                            onChange={(e) => {
                                                setGradientType(e.target.value);
                                                updateGradient(stops);
                                            }}
                                        >
                                            <option value="linear">Linear</option>
                                            <option value="radial">Radial</option>
                                            <option value="conic">Conic</option>
                                        </GradientTypeSelect>
                                        <FontAwesomeIcon icon={faCaretDown} className="select-icon" />
                                    </GradientTypeWrapper>
                                    {gradientType !== 'radial' && (
                                        <AngleControls>
                                            <AngleLabel>Angle:</AngleLabel>
                                            <AngleValue
                                                type="number"
                                                min="0"
                                                max="359"
                                                value={gradientAngle}
                                                onChange={handleAngleChange}
                                            />
                                            <AngleUnitWrapper>
                                                <AngleUnitSelect
                                                    value={angleUnit}
                                                    onChange={(e) => setAngleUnit(e.target.value)}
                                                >
                                                    <option value="deg">deg</option>
                                                    <option value="rad">rad</option>
                                                    <option value="turn">turn</option>
                                                    <option value="grad">grad</option>
                                                </AngleUnitSelect>
                                                <FontAwesomeIcon icon={faCaretDown} className="select-icon" />
                                            </AngleUnitWrapper>
                                        </AngleControls>
                                    )}
                                </AngleInput>
                                <GradientStops>
                                    <HexAlphaColorPicker
                                        color={stops[selectedStop]?.color || stops[0].color}
                                        onChange={(color) => updateStop(selectedStop, { color })}
                                    />
                                    <StopsList>
                                        {stops.map((stop, index) => (
                                            <StopItem key={index}>
                                                <StopColor
                                                    color={stop.color}
                                                    onClick={() => setSelectedStop(index)}
                                                />
                                                <StopPercentage
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={stop.position}
                                                    onChange={(e) => updateStop(index, { position: parseInt(e.target.value) || 0 })}
                                                />
                                                <span>%</span>
                                                <StopActions>
                                                    {index !== 0 && index !== stops.length - 1 && (
                                                        <StopButton onClick={() => removeStop(index)}>
                                                            <FontAwesomeIcon icon={faMinus} />
                                                        </StopButton>
                                                    )}
                                                    {index === stops.length - 2 && (
                                                        <StopButton onClick={addStop}>
                                                            <FontAwesomeIcon icon={faPlus} />
                                                        </StopButton>
                                                    )}
                                                </StopActions>
                                            </StopItem>
                                        ))}
                                    </StopsList>
                                </GradientStops>
                            </>
                        ) : (
                            <HexAlphaColorPicker color={color} onChange={onChange} />
                        )}
                    </ColorPickerContent>
                </ColorPickerPopover>
            )}
        </Column>
    );
};

export default ColorPicker; 